import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, CalendarCheck, Sparkles, Layers, Quote, AlertTriangle,
  CheckCircle2, ChevronDown, MinusCircle, PlusCircle, ArrowDownRight,
  ArrowUpRight,
} from "lucide-react";
import { disciplineBySlug, DISCIPLINES } from "@/config/disciplines";
import { baseById, addonById } from "@/config/clinicOS";
import { useDemo } from "@/components/landing/DemoContext";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { PainPoints, DEFAULT_STATS } from "@/components/welcome/PainPoints";
import { DiscChapters } from "@/components/welcome/DiscChapters";
import { DISCIPLINE_CHAPTERS } from "@/data/discChapters";

/* /disciplines/:slug — industry-specific page.
   Storytelling order: hero · outcomes · the pain · before vs after · workflows
   · recommended stack · typical week · testimonial · FAQ · related industries · CTA */

export default function DisciplinePage() {
  const { slug } = useParams();
  const d = disciplineBySlug(slug);
  const openDemo = useDemo();
  const [openFaq, setOpenFaq] = useState(0);

  if (!d) return <Navigate to="/" replace />;

  const Icon = d.icon;
  const base = baseById(d.recommendedBase);
  const BaseIcon = base.icon;
  const addons = d.recommendedAddons.map(addonById).filter(Boolean);
  const gradient = `linear-gradient(135deg, ${d.fromHex}, ${d.toHex})`;
  const textGrad = { backgroundImage: `linear-gradient(90deg, ${d.fromHex}, ${d.toHex})` };

  return (
    <article data-testid={`discipline-page-${d.slug}`} className="overflow-hidden">

      {/* ───────────── HERO ───────────── */}
      <section className="relative pt-16 pb-8 sm:pt-32 sm:pb-16">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[460px] opacity-30 blur-3xl"
          style={{ background: `radial-gradient(55% 70% at 50% 0%, ${d.fromHex}55, transparent 70%)` }}
        />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="grid items-start gap-6 sm:gap-10 lg:grid-cols-12 lg:gap-12">

            {/* Left — story */}
            <div className="lg:col-span-7">
              <span
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-md"
                style={{ backgroundImage: gradient }}
                data-testid="discipline-eyebrow"
              >
                <Icon className="h-3 w-3" />
                Built for {d.name} clinics
              </span>

              <h1 className="mt-0 sm:mt-5 font-display text-[2.05rem] font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-[3.4rem]">
                {d.tagline.split(" — ")[0]}
                {d.tagline.includes(" — ") && (
                  <>
                    {" "}—{" "}
                    <span className="bg-clip-text text-transparent" style={textGrad}>
                      {d.tagline.split(" — ").slice(1).join(" — ")}
                    </span>
                  </>
                )}
              </h1>
              <p className="mt-3 sm:mt-5 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg">{d.hero}</p>

              <div className="mt-5 sm:mt-7 flex flex-wrap items-center gap-2 sm:gap-3">
                <button
                  onClick={openDemo}
                  data-testid="discipline-book-demo"
                  style={{ backgroundImage: gradient }}
                  className="group inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl px-7 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <CalendarCheck className="h-4 w-4" />
                  Book a {d.name} demo
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </button>
                <Link
                  to="/build"
                  data-testid="discipline-build-cta"
                  className="hidden sm:inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card px-6 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
                >
                  <Layers className="h-4 w-4" />
                  Build your OS
                </Link>
              </div>
            </div>

            {/* Right — outcomes mini-card (desktop only; mobile users scroll directly to the proof) */}
            <div className="hidden lg:col-span-5 lg:block">
              <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7">
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-20 blur-3xl" style={{ backgroundImage: gradient }} />
                <p className="relative text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: d.fromHex }}>
                  What {d.name} clinics see
                </p>
                <h3 className="relative mt-2 font-display text-xl font-black tracking-tight sm:text-2xl">
                  Outcomes from live rollouts
                </h3>
                <div className="relative mt-4 grid grid-cols-2 gap-3">
                  {d.outcomes.map((o, i) => (
                    <motion.div
                      key={o.label}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      className="rounded-2xl border border-border bg-background/60 p-3.5"
                    >
                      <p className="bg-clip-text font-display text-2xl font-black tracking-tight text-transparent sm:text-3xl" style={textGrad}>
                        {o.stat}
                      </p>
                      <p className="mt-1 text-[12px] font-semibold leading-tight">{o.label}</p>
                      <p className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">{o.foot}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── DiSC PAIN-MAPPING ───── */}
      <div className="border-t border-border/60 pt-10 sm:pt-14">
        <PainPoints
          eyebrow={`Inside a ${d.name.toLowerCase()} clinic`}
          heading="The four numbers that change first."
          sub="What an industry-average clinic looks like vs. what Clinic OS clinics in this discipline actually do."
          stats={DEFAULT_STATS}
        />
        <DiscChapters
          eyebrow={`${d.name}, four ways`}
          heading="Same clinic OS. Four reasons it fits your specialty."
          sub="Utilisation, patient commitment, calm front desk, outcome data — pick the chapter that lands for your team."
          chapters={DISCIPLINE_CHAPTERS}
        />
      </div>

      {/* ───────────── THE PAIN ───────────── */}
      <section data-testid="discipline-pains" className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-rose-500">
              The reality
            </p>
            <h2 className="mt-2 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl">
              What hurts in a typical {d.name.toLowerCase()} week.
            </h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {d.pains.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.04] p-5"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-rose-500/15 text-rose-500">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-display text-[15px] font-black tracking-tight">{p.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── BEFORE vs AFTER ───────────── */}
      <section data-testid="discipline-comparison" className="border-t border-border bg-secondary/30 py-16 dark:bg-transparent sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: d.fromHex }}>
              Before · After
            </p>
            <h2 className="mt-2 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl">
              What changes once Clinic OS is live.
            </h2>
          </div>

          <div className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
            <div className="grid grid-cols-3 border-b border-border bg-secondary/40 px-5 py-3 text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground sm:px-6">
              <div>Metric</div>
              <div className="flex items-center gap-1.5"><MinusCircle className="h-3 w-3 text-rose-500" /> Before</div>
              <div className="flex items-center gap-1.5"><PlusCircle className="h-3 w-3 text-emerald-500" /> After</div>
            </div>
            {d.comparison.map((row, i) => (
              <motion.div
                key={row.metric}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="grid grid-cols-3 border-b border-border/60 px-5 py-3.5 text-[13px] last:border-b-0 sm:px-6"
              >
                <p className="font-semibold">{row.metric}</p>
                <p className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <ArrowDownRight className="h-3.5 w-3.5 text-rose-500" />
                  {row.before}
                </p>
                <p className="inline-flex items-center gap-1.5 font-semibold">
                  <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />
                  {row.after}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── WORKFLOWS ───────────── */}
      <section data-testid="discipline-workflows" className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: d.fromHex }}>
              Industry workflows
            </p>
            <h2 className="mt-2 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl">
              The {d.name.toLowerCase()} flows we automate by default.
            </h2>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
              Out of the box — each one calibrated to your clinic in the first 14 days.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.workflows.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.3, delay: (i % 3) * 0.04 }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
              >
                <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-0 blur-3xl transition-opacity group-hover:opacity-30" style={{ backgroundImage: gradient }} />
                <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15" style={{ backgroundImage: gradient }}>
                  <w.Icon className="h-5 w-5" />
                </span>
                <h3 className="relative mt-3.5 font-display text-[15px] font-black tracking-tight">{w.title}</h3>
                <p className="relative mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">{w.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── RECOMMENDED STACK ───────────── */}
      <section data-testid="discipline-stack" className="border-t border-border bg-secondary/30 py-16 dark:bg-transparent sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 sm:px-8 lg:grid-cols-[1fr_1.2fr] lg:px-12">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: d.fromHex }}>
              Our recommendation
            </p>
            <h2 className="mt-2 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl">
              The Clinic OS stack we&apos;d build for you.
            </h2>
            <ul className="mt-5 space-y-2.5 text-[14px]">
              {d.why.map((w) => (
                <li key={w} className="flex items-start gap-2.5">
                  <Sparkles className="mt-0.5 h-4 w-4 flex-none" style={{ color: d.fromHex }} />
                  <span className="text-foreground/85">{w}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/build"
              className="mt-6 inline-flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-5 text-[13px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
            >
              <Layers className="h-4 w-4" />
              Configure my stack
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="space-y-3">
            {/* Base — highlighted */}
            <Link
              to={`/products/${base.id}`}
              className="group flex items-center gap-4 rounded-2xl border-2 bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
              style={{ borderColor: `${d.fromHex}55` }}
            >
              <span className={`inline-flex h-12 w-12 flex-none items-center justify-center rounded-xl ${base.bg} ${base.accent}`}>
                <BaseIcon className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-lg font-black tracking-tight">{base.name}</span>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white shadow-sm" style={{ backgroundImage: gradient }}>
                    Recommended base
                  </span>
                </div>
                <p className="mt-1 text-[13px] text-muted-foreground">{base.tagline}</p>
              </div>
              <ArrowRight className="h-4 w-4 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>

            {/* Add-ons */}
            {addons.map((a) => {
              const AIcon = a.icon;
              return (
                <Link
                  key={a.id}
                  to={`/products/${a.id}`}
                  className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-md"
                >
                  <span className={`inline-flex h-12 w-12 flex-none items-center justify-center rounded-xl ${a.bg} ${a.accent}`}>
                    <AIcon className="h-6 w-6" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="font-display text-[15px] font-black tracking-tight">{a.name}</span>
                    <p className="mt-0.5 line-clamp-1 text-[13px] text-muted-foreground">{a.tagline}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── TYPICAL WEEK ───────────── */}
      <section data-testid="discipline-week" className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6 sm:px-8 lg:px-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-500">
            A week running on Clinic OS
          </p>
          <h2 className="mt-2 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl">
            What changes for you.
          </h2>
          <ol className="mt-8 space-y-5 border-l-2 pl-6" style={{ borderColor: `${d.fromHex}33` }}>
            {d.typicalDay.map((t, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="relative"
              >
                <span className="absolute -left-[31px] top-1.5 inline-flex h-3 w-3 rounded-full ring-4 ring-background" style={{ backgroundImage: gradient }} />
                <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: d.fromHex }}>{t.time}</p>
                <p className="mt-1 text-[15px] leading-relaxed text-foreground/90">{t.event}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* ───────────── QUOTE ───────────── */}
      {d.quote && (
        <section className="border-t border-border bg-secondary/30 py-16 dark:bg-transparent sm:py-20">
          <div className="mx-auto max-w-3xl px-6 text-center sm:px-8 lg:px-12">
            <Quote className="mx-auto h-9 w-9" style={{ color: d.fromHex }} />
            <blockquote className="mt-4 font-display text-2xl font-black leading-tight tracking-tight sm:text-3xl">
              &ldquo;{d.quote.text}&rdquo;
            </blockquote>
            <p className="mt-4 text-[12.5px] text-muted-foreground">— {d.quote.author}</p>
          </div>
        </section>
      )}

      {/* ───────────── FAQ ───────────── */}
      <section data-testid="discipline-faq" className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6 sm:px-8 lg:px-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: d.fromHex }}>
            Common questions
          </p>
          <h2 className="mt-2 font-display text-3xl font-black tracking-[-0.02em] sm:text-4xl">
            What {d.name} clinic owners usually ask.
          </h2>
          <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
            {d.faqs.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <button
                  key={f.q}
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  data-testid={`discipline-faq-${i}`}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
                    <h3 className="font-display text-[15px] font-bold tracking-tight">{f.q}</h3>
                    <ChevronDown className={`mt-0.5 h-4 w-4 flex-none text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden px-5 pb-5 text-[13.5px] leading-relaxed text-muted-foreground sm:px-6 sm:pb-6"
                      >
                        {f.a}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── OTHER INDUSTRIES ───────────── */}
      <section className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-violet-500">
            Also built for
          </p>
          <h2 className="mt-2 font-display text-2xl font-black tracking-[-0.02em] sm:text-3xl">
            Other clinics we serve.
          </h2>
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DISCIPLINES.filter((x) => x.slug !== d.slug).map((o) => {
              const OIcon = o.icon;
              return (
                <Link
                  key={o.slug}
                  to={`/disciplines/${o.slug}`}
                  data-testid={`discipline-related-${o.slug}`}
                  className="group flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-md"
                >
                  <span
                    className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15"
                    style={{ backgroundImage: `linear-gradient(135deg, ${o.fromHex}, ${o.toHex})` }}
                  >
                    <OIcon className="h-4 w-4" />
                  </span>
                  <span className="font-display text-[14px] font-black tracking-tight">{o.name}</span>
                  <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
    </article>
  );
}
